/**
 * useReplayPlayback — fetches an NDJSON replay and exposes a playback
 * cursor (currentIndex), play/pause/seek/speed controls, and a derived
 * "events up to cursor" slice for downstream views.
 *
 * Format: line 0 is `{ type: 'match_meta', ... }`; lines 1..N are
 * BaseEvent objects with { kind, payload, at, seq }. Timestamps drive
 * the playback clock; speed scales wall-clock advance.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiPath } from '../api.js';

export type ReplayEvent = {
  kind: string;
  payload: Record<string, unknown>;
  at: number;
  seq?: number;
  actor?: string;
};

export type ReplayMeta = {
  matchUlid: string;
  players: readonly string[];
  gameId: string;
  winner: string;
  endedAt: number;
};

export type ReplayPlaybackHook = {
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  meta: ReplayMeta | null;
  events: readonly ReplayEvent[];
  /** Index of the latest event that has "happened" in playback time. */
  cursor: number;
  /** All events at index ≤ cursor (already-played slice). */
  played: readonly ReplayEvent[];
  playing: boolean;
  speed: number;
  setSpeed: (s: number) => void;
  play: () => void;
  pause: () => void;
  toggle: () => void;
  /** Jump to event index (0-based). Pauses on jump. */
  seekTo: (idx: number) => void;
  /** Step to the next or previous TableActionApplied event. */
  stepNext: () => void;
  stepPrev: () => void;
};

export function useReplayPlayback(matchUlid: string): ReplayPlaybackHook {
  const [status, setStatus] = useState<ReplayPlaybackHook['status']>('loading');
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<ReplayMeta | null>(null);
  const [events, setEvents] = useState<readonly ReplayEvent[]>([]);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);

  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Fetch + parse NDJSON on mount ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiPath(`/api/match/${matchUlid}/replay`), {
          credentials: 'include',
        });
        if (!res.ok) {
          throw new Error(`replay fetch returned ${res.status}`);
        }
        const text = await res.text();
        if (cancelled) return;
        const lines = text.split('\n').filter((l) => l.length > 0);
        if (lines.length === 0) throw new Error('empty replay');
        const head = JSON.parse(lines[0] ?? '{}');
        if (head.type !== 'match_meta') {
          throw new Error('first line is not match_meta');
        }
        const parsedEvents: ReplayEvent[] = [];
        for (const line of lines.slice(1)) {
          try {
            parsedEvents.push(JSON.parse(line) as ReplayEvent);
          } catch {
            // Skip malformed lines silently — replays are best-effort.
          }
        }
        // Sort by `at` to defend against out-of-order writes; ties broken by seq.
        parsedEvents.sort((a, b) => a.at - b.at || (a.seq ?? 0) - (b.seq ?? 0));
        setMeta({
          matchUlid: head.matchUlid,
          players: head.players ?? [],
          gameId: head.gameId,
          winner: head.winner,
          endedAt: head.endedAt,
        });
        setEvents(parsedEvents);
        setCursor(0);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setStatus('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [matchUlid]);

  // ─── Playback tick ─────────────────────────────────────────────────
  useEffect(() => {
    if (!playing || events.length === 0) {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      return;
    }
    // Each tick advances by 1 event scaled by speed: at speed=1, one
    // event per 800ms (eyeballed for readability). Higher speeds are
    // faster.
    const intervalMs = Math.max(50, 800 / speed);
    tickRef.current = setInterval(() => {
      setCursor((c) => {
        if (c >= events.length - 1) {
          setPlaying(false);
          return events.length - 1;
        }
        return c + 1;
      });
    }, intervalMs);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [playing, speed, events.length]);

  const play = useCallback(() => {
    if (events.length === 0) return;
    if (cursor >= events.length - 1) setCursor(0);
    setPlaying(true);
  }, [cursor, events.length]);

  const pause = useCallback(() => setPlaying(false), []);
  const toggle = useCallback(() => {
    if (playing) pause();
    else play();
  }, [playing, pause, play]);

  const seekTo = useCallback(
    (idx: number) => {
      setPlaying(false);
      setCursor(Math.max(0, Math.min(events.length - 1, idx)));
    },
    [events.length],
  );

  const stepNext = useCallback(() => {
    setPlaying(false);
    setCursor((c) => Math.min(events.length - 1, c + 1));
  }, [events.length]);

  const stepPrev = useCallback(() => {
    setPlaying(false);
    setCursor((c) => Math.max(0, c - 1));
  }, []);

  const played = events.slice(0, cursor + 1);

  return {
    status,
    error,
    meta,
    events,
    cursor,
    played,
    playing,
    speed,
    setSpeed,
    play,
    pause,
    toggle,
    seekTo,
    stepNext,
    stepPrev,
  };
}
